import { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import WorkoutsPage from '@/pages/WorkoutsPage';
import WorkoutPage from '@/pages/WorkoutPage';
import NewWorkoutPage from '@/pages/NewWorkoutPage';
import ExercisesPage from '@/pages/ExercisesPage';
import MeasurementsPage from '@/pages/MeasurementsPage';
import NutritionPage from '@/pages/NutritionPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPage from '@/pages/AdminPage';
import LoadingSpinner from '@/components/LoadingSpinner';
import LoadingScreen from '@/components/LoadingScreen';
import FullscreenTimer from '@/components/FullscreenTimer';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { user, loading } = useAuth();
  const [isPreloaded, setIsPreloaded] = useState(false);

  // Show loading screen during auth check AND data preload for authenticated users
  if (loading || (user && !isPreloaded)) {
    return (
      <LoadingScreen 
        isAuthLoading={loading}
        onComplete={() => setIsPreloaded(true)} 
      />
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="workouts" element={<WorkoutsPage />} />
          <Route path="workouts/new" element={<NewWorkoutPage />} />
          <Route path="workouts/:id" element={<WorkoutPage />} />
          <Route path="exercises" element={<ExercisesPage />} />
          <Route path="measurements" element={<MeasurementsPage />} />
          <Route path="nutrition" element={<NutritionPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route
            path="admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Global fullscreen timer overlay */}
      <FullscreenTimer />
    </>
  );
}
